import React, { useState } from 'react';
import { useParams } from 'react-router';
import { useSelector } from 'react-redux';
import { List } from 'immutable';

// Types
import { ReviewTemplateVersion } from 'Shared/types/ReviewTemplates.type';

// Selectors
import * as ReviewsSelectors from 'Shared/selectors/Reviews.selectors';
import * as UserSelectors from 'Shared/selectors/Users.selectors';

// Hooks
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useReview } from 'Shared/utils/hooks/use-review';

// Components
import SharePageReviewForm from './_partials/SharePageReviewForm';
import SelfReviewContainer from './SelfReviewContainer';
import { WpText } from 'Wpds/components/text';
import { WpRichTextInput } from 'Wpds/components/rich-text';
import PrintPageTemplate, { PrintOption } from 'Shared/components/PrintPageTemplate/PrintPageTemplate';
import PrintPageSectionTitle from 'Shared/components/PrintPageTemplate/PrintPageSectionTitle';
import SharePageQuestionItem from './SharePageQuestionReview/SharePageQuestionItem';
import ManagerReviewFormContainer from './ManagerReviewFormContainer/ManagerReviewFormContainer';

// Utils
import * as ReviewsUtils from 'Shared/utils/Reviews.utils';
import { isEmpty } from 'Wpds/components/rich-text/utils/EditorState.utils';
import { formatDate } from 'Shared/utils/DateTime.utils';

// Helpers
import * as ReviewFormTemplatesHelpers from 'Shared/model_helpers/ReviewFormTemplates.helper';
import * as ReviewFormHelpers from 'Shared/model_helpers/ReviewForm.helper';
import * as ReviewsHelpers from 'Shared/model_helpers/Reviews.helper';
import * as UserHelpers from 'Shared/model_helpers/User.helpers';

// Styles
import './ThreeSixtySharePageForPrint.scss';

type Props = {
  review;
  togglePrintPreview: () => void;
};

const ThreeSixtySharePageForPrint = ({ review, togglePrintPreview }: Props) => {
  const { reviewSlug } = useParams();
  const { slug: currentUserSlug } = useCurrentUser();

  const { reviewTemplateVersion, selfPeerReviewForm, reviewJsObject, isPerformanceReview } = useReview({
    slug: reviewSlug,
  });

  enum PrintOptionTypes {
    summary = 'summary',
    individual_responses = 'individual_responses',
    self_review = 'self_review',
    performance_review_responses = 'performance_review_responses',
  }

  const [selectedPrintOption, setSelectedPrintOption] = useState<string>(PrintOptionTypes.summary);

  const self360 = ReviewsHelpers.getSelf360(review);
  const receiverSlug = ReviewsHelpers.getReceiverSlug(review);

  const selfReivewFormSubmittedAt = ReviewFormHelpers.getSubmittedAt(selfPeerReviewForm);
  const isPeerReviewSharedAnonymously = ReviewsUtils.isFeedbackSharedAnonymously({ review });

  const selfReviewTemplate = ReviewsUtils.getFormTemplateForForm({ review, templateFormType: 'peer_review' });
  const compilationForm = useSelector(state =>
    ReviewsSelectors.getReviewFormsByReviewSlugAndType(state, { reviewSlug, formType: 'compilation' }).first()
  );
  const questions = ReviewFormTemplatesHelpers.getQuestions(selfReviewTemplate) || List();
  const introduction = ReviewFormHelpers.getIntroduction(compilationForm);
  const compilationSubmittedAt = ReviewFormHelpers.getSubmittedAt(compilationForm);

  const creatorSlug = ReviewsHelpers.getCreatorSlug(review);

  const compilerSlug = ReviewsHelpers.getCompilerSlug(review);
  const compiler = useSelector(state => UserSelectors.getUserByUserSlug(state, { userSlug: compilerSlug }));
  const compilerName = UserHelpers.getFullName(compiler);

  const receiver = useSelector(state => UserSelectors.getUserByUserSlug(state, { userSlug: receiverSlug }));
  const receiverName = UserHelpers.getFullName(receiver);
  const titlePerson = self360 ? 'Self' : `${receiverName}'s`;
  const title = isPerformanceReview ? `${receiverName}'s review` : `${titlePerson} 360 Feedback`;

  const isCurrentUserReceiver = currentUserSlug === receiverSlug;
  const isCurrentUserCompiler = currentUserSlug === compilerSlug;
  const isCurrentUserCreator = currentUserSlug === creatorSlug;

  const unlockedAt = ReviewsHelpers.getUnlockedAt(review);

  const showIntroduction = !isEmpty(introduction);

  if (!review) return null;

  const baseClassName = 'ThreeSixtySharePageForPrint';

  const SummaryPage = (
    <>
      {showIntroduction && (
        <>
          <PrintPageSectionTitle title="Introduction" />
          <div style={{ lineHeight: '1.75', marginBottom: '1.5rem' }}>
            <WpRichTextInput
              value={introduction}
              readOnly
              options={{ showBorder: false, overflowBehavior: 'grow' }}
              height="0"
            />
          </div>
        </>
      )}
      <PrintPageSectionTitle title="Summary" />
      <SharePageReviewForm review={review} />
    </>
  );

  const individualResponsesPages = questions.map(question => (
    <>
      <PrintPageSectionTitle
        superScript="individual responses"
        title={ReviewsUtils.generateHeaderForQuestion({ question })}
      />
      <SharePageQuestionItem review={review} question={question} showInContainer={false} showQuestionHeader={false} />
    </>
  ));

  const SelfReviewPage = (
    <>
      <PrintPageSectionTitle title="Self Review" />
      <SelfReviewContainer review={review} inContainer={false} showTitle={false} />
    </>
  );

  const ManagerReviewPage = (
    <>
      <PrintPageSectionTitle title="Manager Review" />
      <ManagerReviewFormContainer reviewSlug={reviewSlug} inContainer={false} />
    </>
  );

  const pages = (() => {
    if (self360) return individualResponsesPages;

    switch (selectedPrintOption) {
      case PrintOptionTypes.individual_responses:
        if (reviewTemplateVersion === ReviewTemplateVersion.two && selfPeerReviewForm)
          return [SummaryPage, SelfReviewPage, ...individualResponsesPages];
        else return [SummaryPage, ...individualResponsesPages];
      case PrintOptionTypes.self_review:
        return [SummaryPage, SelfReviewPage];
      case PrintOptionTypes.performance_review_responses:
        return [SummaryPage, SelfReviewPage, ManagerReviewPage];
      default:
        return [SummaryPage];
    }
  })();

  const printOptions = ((): PrintOption[] => {
    const summaryOption: PrintOption = { label: 'Summary Only', value: PrintOptionTypes.summary };
    const selfReviewOption: PrintOption = { label: 'Summary + Self Review', value: PrintOptionTypes.self_review };
    const individualResponsesOption: PrintOption = {
      label: 'Summary + Individual Responses',
      value: PrintOptionTypes.individual_responses,
    };
    const performanceReviewOption: PrintOption = {
      label: 'Summary + Reviews',
      value: PrintOptionTypes.performance_review_responses,
    };

    if (isPerformanceReview) return [summaryOption, performanceReviewOption];
    if (self360) return null;
    if (isCurrentUserReceiver && isPeerReviewSharedAnonymously) return [summaryOption, individualResponsesOption];
    if (isCurrentUserReceiver && selfReivewFormSubmittedAt && !isPeerReviewSharedAnonymously)
      return [summaryOption, selfReviewOption];
    if (isCurrentUserCompiler || isCurrentUserCreator) return [summaryOption, individualResponsesOption];
    return null;
  })();

  const Feedback360TitleMeta = (
    <div className={`${baseClassName}__title-meta`}>
      {self360 ? (
        <div className={`${baseClassName}__title-meta__item`}>
          <WpText bold>Reviewee:</WpText>
          <WpText>{receiverName}</WpText>
        </div>
      ) : (
        <>
          <div className={`${baseClassName}__title-meta__item`}>
            <WpText bold>Date Completed:</WpText>
            <WpText>{formatDate(unlockedAt, 'MMM Do, YYYY')}</WpText>
          </div>
          <div className={`${baseClassName}__title-meta__item`}>
            <WpText bold>Summarized By:</WpText>
            <WpText>{compilerName}</WpText>
          </div>
        </>
      )}
    </div>
  );

  const PerformanceReviewTitleMeta = (
    <div className={`${baseClassName}__title-meta`}>
      <div className={`${baseClassName}__title-meta__item`}>
        <WpText>{reviewJsObject.reviewCycleName}</WpText>
      </div>
      <div className={`${baseClassName}__title-meta__item`}>
        <WpText bold>Date Completed:</WpText>
        <WpText>{formatDate(compilationSubmittedAt, 'MMM Do, YYYY')}</WpText>
      </div>
    </div>
  );

  return (
    <PrintPageTemplate
      title={title}
      pages={pages}
      titleMeta={isPerformanceReview ? PerformanceReviewTitleMeta : Feedback360TitleMeta}
      selectedPrintOption={selectedPrintOption}
      printOptions={!self360 && printOptions}
      handleUpdatePrintOption={setSelectedPrintOption}
      handleClosePrintPreviewWindow={togglePrintPreview}
    />
  );
};

export default ThreeSixtySharePageForPrint;
